<template>
  <div class="asa-data-export">
    <h3>ASA数据导出</h3>

    <el-form
      :inline="true"
      :model="exportForm"
      style="width: 100%"
      label-position="right"
      label-width="auto"
    >
      <el-row :gutter="20">
        <el-col :span="8">
          <el-form-item label="时间范围:" style="width: 100%">
            <el-date-picker
              v-model="exportForm.date"
              type="daterange"
              unlink-panels
              size="default"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              :shortcuts="shortcuts"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              :disabled-date="disabledDate"
              @change="handleDateChange"
              style="height: 32px; width: 100%"
            />
          </el-form-item>
        </el-col>

        <el-col :span="5">
          <el-form-item label="项目:" style="width: 100%">
            <el-select
              v-model="exportForm.p"
              placeholder="选择项目"
              filterable
              allow-create
              style="width: 100%"
            >
              <el-option
                v-for="item in project_options"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
        </el-col>

        <el-col :span="6">
          <el-form-item label="发送到邮箱:" style="width: 100%">
            <el-checkbox v-model="exportForm.sendToEmail" style="margin-right: 10px">
              <span>启用</span>
              <el-tooltip content="勾选后，数据将在后台生成并自动发送到指定邮箱，您可以选择关闭页面" placement="top">
                <el-icon style="margin-left: 4px; color: #909399; cursor: help;">
                  <InfoFilled />
                </el-icon>
              </el-tooltip>
            </el-checkbox>
            <el-input
              v-model="exportForm.email"
              placeholder="请输入邮箱地址"
              :disabled="!exportForm.sendToEmail"
              style="width: calc(100% - 80px)"
              clearable
            >
              <template #prefix>
                <el-icon><Message /></el-icon>
              </template>
            </el-input>
          </el-form-item>
        </el-col>

        <el-col :span="5">
          <el-form-item>
            <el-button
              type="primary"
              @click="export_asa_data"
              :loading="exporting"
              :disabled="exporting || taskState.active"
              style="width: 200px"
            >
              {{ exporting ? '正在创建…' : (taskState.active ? '正在后台生成' : '创建导出任务') }}
            </el-button>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>

        <div class="remark-text" style="margin-bottom: 5px">
      <el-tooltip class="box-item" effect="dark" placement="top-start" width="500">
        <template #content>
          <div style="line-height: 1.8; padding: 5px;">
            <strong>📋 数据导出说明：</strong>
            <ol style="margin: 8px 0; padding-left: 20px;">
              <li>数据导出包含系列数据和关键词数据</li>
              <li>每天的数据将分别放在不同的工作表中</li>
              <li>工作表命名格式：日期_系列 和 日期_关键词</li>
              <li>Excel文件命名格式：asa_开始日期_结束日期_项目.xlsx</li>
            </ol>
            <strong>💡 导出模式说明：</strong>
            <ul style="margin: 8px 0; padding-left: 20px;">
              <li><strong>统一后台任务模式</strong>：所有日期范围均先创建后台任务，页面自动轮询状态；完成后可下载，勾选邮箱时会额外发送邮件</li>
            </ul>
            <strong>⚠️ 注意事项：</strong>
            <ul style="margin: 8px 0; padding-left: 20px;">
              <li>最大支持导出90天数据</li>
              <li>最大支持导出90天；邮箱为可选的额外交付方式</li>
              <li>后台任务在服务重启后无法恢复，超过30分钟无状态更新会标记失败</li>
            </ul>
          </div>
        </template>
        <el-button style="color: #409EFF; border: none; background: none; padding: 0;">
          <span style="display: flex; align-items: center;">
            <el-icon style="margin-right: 4px;"><InfoFilled /></el-icon>
            使用说明
          </span>
        </el-button>
      </el-tooltip>
    </div>

    <section
      v-if="showTaskCard"
      class="export-task-card"
      :class="`is-${taskDisplay.tone}`"
    >
      <p class="sr-only" role="status" aria-live="polite">{{ taskState.liveText }}</p>
      <div class="task-card-header">
        <div class="task-card-status">
          <span class="task-status-icon" aria-hidden="true">{{ taskDisplay.icon }}</span>
          <span class="task-status-label">{{ taskDisplay.label }}</span>
        </div>
        <el-button
          v-if="taskState.resultAvailable"
          type="primary"
          class="task-card-action"
          @click="downloadTaskResult"
        >下载 Excel</el-button>
        <el-button
          v-else-if="taskDisplay.canRetry"
          class="task-card-action"
          @click="export_asa_data"
        >重新提交</el-button>
      </div>

      <div class="task-card-main">
        <strong>{{ taskDisplay.title }}</strong>
        <p>{{ taskDisplay.description }}</p>
      </div>

      <div v-if="taskDisplay.showProgress" class="task-progress-section">
        <div class="task-progress-meta">
          <span>{{ taskDisplay.stage }}</span>
          <span>{{ exportProgress.percentage }}%</span>
        </div>
        <div
          class="task-progress-track"
          role="progressbar"
          :aria-valuenow="exportProgress.percentage"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-label="`导出进度 ${exportProgress.percentage}%`"
        >
          <span class="task-progress-value" :style="{ width: `${exportProgress.percentage}%` }"></span>
        </div>
      </div>
      <div v-else-if="taskDisplay.showPending" class="task-pending-line" role="status">
        <span class="sr-only">正在等待任务资源</span>
        <span class="task-pending-dot" aria-hidden="true"></span><span class="task-pending-dot" aria-hidden="true"></span><span class="task-pending-dot" aria-hidden="true"></span>
      </div>

      <p v-if="taskDisplay.currentDateText" class="task-current-date">{{ taskDisplay.currentDateText }}</p>
      <p v-if="taskDisplay.downloadHint" class="task-download-hint">{{ taskDisplay.downloadHint }}</p>

      <div v-if="taskState.summary" class="task-summary" aria-label="导出任务摘要">
        <span>{{ taskState.summary.project }}</span>
        <span>{{ taskState.summary.dateRange }}</span>
        <span v-if="taskState.summary.email">完成后发送至邮箱</span>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, ref, reactive, onMounted, onBeforeUnmount } from 'vue'
import {
  export_asa_data_excel,
  get_asa_export_task,
  download_asa_export_task,
  clear_asa_export_task_result
} from '../services/requests'
import { ElMessage } from 'element-plus'
import { InfoFilled, Message } from '@element-plus/icons-vue'

// 项目选项
const project_options = [
  { label: 'MetroVPN', value: 'MetroVPN' },
  { label: 'PDFConverter', value: 'PDFConverter' },
  { label: 'Pixeta', value: 'Pixeta' },
  { label: 'LineVPN', value: 'LineVPN' }
]

// 日期快捷选项
const shortcuts = [
  {
    text: '最近一周',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
      return [start, end]
    }
  },
  {
    text: '最近一个月',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
      return [start, end]
    }
  },
  {
    text: '最近三个月',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
      return [start, end]
    }
  }
]

// 导出表单
const exportForm = reactive({
  date: [],
  p: 'PDFConverter',
  sendToEmail: false,
  email: ''
})

// 导出状态
const exporting = ref(false)
const TASK_STORAGE_KEY = 'asa-export-active-task'
const taskState = reactive({
  id: '',
  capability: '',
  active: false,
  resultAvailable: false,
  filename: '',
  status: '',
  summary: null,
  pollingError: false,
  downloadStarted: false,
  restored: false,
  creationError: '',
  failureReason: '',
  liveText: '',
  liveKey: ''
})
let taskPollTimer = null
let polling = false

const exportProgress = reactive({
  show: false,
  message: '',
  type: 'info',
  description: '',
  percentage: 0,
  currentDate: '',
  currentIndex: 0,
  total: 0,
  status: ''
})

// 常量
const MAX_DAYS = 90  // 最大日期范围：3个月

function friendlyTaskMessage(message) {
  if (!message) return '正在准备导出数据'
  if (message.includes('连接数据库') || message.includes('加载')) return '正在加载导出数据'
  if (message.includes('关键词')) return '正在汇总关键词数据'
  if (message.includes('系列')) return '正在汇总系列数据'
  if (message.includes('生成Excel') || message.includes('写入')) return '正在写入 Excel 文件'
  if (message.includes('保存') || message.includes('准备发送')) return '正在保存导出文件'
  return '正在处理导出数据'
}

function safeTaskError(message, fallback) {
  const text = String(message || '')
  if (text.includes('日期范围不能超过')) return '日期范围不能超过 90 天'
  if (text.includes('邮箱地址')) return '邮箱地址格式不正确'
  if (text.includes('权限') || text.includes('认证')) return '当前会话已失效，请刷新页面后重试'
  if (text.includes('任务状态已过期')) return '任务已过期，请重新提交导出任务'
  if (text.includes('timeout') || text.includes('超时')) return '导出处理超时，请缩短日期范围后重试'
  if (text.includes('connection') || text.includes('连接')) return '数据源暂时不可用，请稍后重试'
  if (text.includes('邮件')) return '邮件发送未成功'
  return fallback
}

function updateLiveAnnouncement(key, text) {
  if (taskState.liveKey !== key) {
    taskState.liveKey = key
    taskState.liveText = text
  }
}

function getTaskDisplayModel() {
  const status = taskState.status
  if (taskState.pollingError) {
    return {
      tone: 'warning', icon: '!', label: '连接暂时中断', title: '暂时无法更新进度',
      description: '将自动重试，不会中断后台导出', showProgress: false, showPending: false
    }
  }
  if (status === 'creation_failed') {
    return {
      tone: 'danger', icon: '!', label: '创建失败', title: '未能创建导出任务',
      description: taskState.creationError || '请稍后重试',
      showProgress: false, showPending: false, canRetry: true
    }
  }
  if (exporting.value && !taskState.id) {
    return {
      tone: 'info', icon: '…', label: '正在创建', title: '正在创建导出任务',
      description: '请稍候…', showProgress: false, showPending: true
    }
  }
  if (status === 'pending') {
    return {
      tone: 'info', icon: '…', label: '排队中', title: '导出任务已提交',
      description: taskState.restored
        ? '已恢复上次导出任务，正在等待任务资源'
        : '正在等待任务资源；可离开本页，任务会继续执行',
      showProgress: false, showPending: true
    }
  }
  if (status === 'running') {
    const hasDate = Boolean(exportProgress.currentDate)
    return {
      tone: 'info', icon: '↻', label: hasDate ? '正在生成' : '正在准备',
      title: hasDate ? '正在生成 Excel' : '正在准备导出数据',
      description: hasDate ? '' : friendlyTaskMessage(exportProgress.message),
      stage: friendlyTaskMessage(exportProgress.message),
      showProgress: true, showPending: false,
      currentDateText: hasDate
        ? `正在处理：${exportProgress.currentDate} · 第 ${exportProgress.currentIndex}/${exportProgress.total} 天`
        : ''
    }
  }
  if (status === 'completed') {
    if (taskState.resultAvailable) {
      return {
        tone: 'success', icon: '✓', label: '已完成', title: 'Excel 已生成',
        description: '文件已准备好，可立即下载', showProgress: false, showPending: false,
        downloadHint: '下载后将清理服务器临时文件'
      }
    }
    if (taskState.downloadStarted) {
      return {
        tone: 'success', icon: '✓', label: '已完成', title: '已开始下载',
        description: '文件下载已开始', showProgress: false, showPending: false
      }
    }
    return {
      tone: 'muted', icon: 'i', label: '文件已过期', title: '临时文件已清理',
      description: '请重新提交导出任务', showProgress: false, showPending: false, canRetry: true
    }
  }
  if (status === 'failed') {
    if (taskState.resultAvailable) {
      return {
        tone: 'warning', icon: '!', label: '邮件未送达', title: 'Excel 已生成',
        description: '邮件发送未成功，但文件仍可下载', showProgress: false, showPending: false,
        downloadHint: '下载后将清理服务器临时文件'
      }
    }
    return {
      tone: 'danger', icon: '!', label: '导出失败', title: '未能生成 Excel',
      description: taskState.failureReason || '请重新提交导出任务',
      showProgress: false, showPending: false, canRetry: true
    }
  }
  return {
    tone: 'info', icon: 'i', label: '导出任务', title: '正在准备导出任务',
    description: '请稍候…', showProgress: false, showPending: false
  }
}

const showTaskCard = computed(() => exporting.value || Boolean(taskState.status) || exportProgress.show)
const taskDisplay = computed(() => getTaskDisplayModel())

// 初始化日期范围为最近一周
onMounted(() => {
  const end = new Date()
  const start = new Date()
  start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
  exportForm.date = [
    formatDate(start),
    formatDate(end)
  ]
  restoreTaskReference()
})

// 格式化日期
function formatDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// 计算日期天数
function calculateDays(startDate, endDate) {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const diffTime = Math.abs(end - start)
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
}

// 禁用日期（限制最大3个月）
function disabledDate(time) {
  const selectedStart = exportForm.date && exportForm.date[0] ? new Date(exportForm.date[0]) : null
  const date = new Date(time)
  
  if (selectedStart) {
    // 如果已选择开始日期，限制结束日期最多90天后
    const maxEndDate = new Date(selectedStart)
    maxEndDate.setDate(maxEndDate.getDate() + MAX_DAYS - 1)
    return date > maxEndDate
  }
  
  return false
}

// 日期变化监听
function handleDateChange() {
  // 日期范围已由 disabledDate 限制，提交后统一创建后台任务。
}

// 导出ASA数据
async function export_asa_data() {
  // 1. 验证日期
  if (!exportForm.date || exportForm.date.length !== 2) {
    ElMessage.error('请选择日期范围')
    return
  }

  // 2. 验证项目
  if (!exportForm.p) {
    ElMessage.error('请选择项目')
    return
  }

  // 3. 验证日期范围
  const days = calculateDays(exportForm.date[0], exportForm.date[1])
  if (days > MAX_DAYS) {
    ElMessage.error(`日期范围不能超过3个月（${MAX_DAYS}天）`)
    return
  }

  // 4. 验证邮箱（如果勾选了发送到邮箱）
  if (exportForm.sendToEmail) {
    if (!exportForm.email || exportForm.email.trim() === '') {
      ElMessage.error({
        message: '请输入接收文件的邮箱地址',
        duration: 3000
      })
      return
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(exportForm.email)) {
      ElMessage.error({
        message: '邮箱地址格式不正确，请检查（如：example@domain.com）',
        duration: 3000
      })
      return
    }
  }

  stopTaskPolling()
  clearTaskReference()
  taskState.resultAvailable = false
  taskState.status = ''
  taskState.pollingError = false
  taskState.downloadStarted = false
  taskState.restored = false
  taskState.creationError = ''
  taskState.failureReason = ''
  updateLiveAnnouncement('creating', '正在创建导出任务')
  taskState.summary = {
    project: exportForm.p,
    dateRange: `${exportForm.date[0]} 至 ${exportForm.date[1]}`,
    email: exportForm.sendToEmail
  }
  exporting.value = true
  
  // 立即显示进度信息
  exportProgress.show = true
  exportProgress.message = '开始处理...'
  exportProgress.type = 'info'
  
  const start_date = exportForm.date[0]
  const end_date = exportForm.date[1]
  const p = exportForm.p
  const email = exportForm.sendToEmail ? exportForm.email.trim() : ''
  
  exportProgress.description = '正在提交后台导出任务，请稍候…'

  try {
    // 调用统一导出接口
    const response = await export_asa_data_excel({
      start_date: start_date,
      end_date: end_date,
      p: p,
      email: email
    })

    // 统一异步接口只接受任务 JSON；不会再返回 Excel ArrayBuffer。
    const jsonData = response.data
    exportProgress.message = '正在创建后台任务...'
    exportProgress.type = 'info'

    if (jsonData && jsonData.code === 200 && jsonData.data && jsonData.data.task_id && jsonData.data.task_capability) {
        taskState.id = jsonData.data.task_id
        taskState.capability = jsonData.data.task_capability
        taskState.active = true
        taskState.status = 'pending'
        taskState.liveText = ''
        persistTaskReference()
        exportProgress.message = '导出任务已提交'
        exportProgress.type = 'info'
        exportProgress.description = '正在等待任务资源'
        ElMessage.success('后台导出任务已创建，正在获取进度')
        startTaskPolling()
    } else {
      const errorMsg = safeTaskError(
        jsonData && jsonData.message, '暂时无法创建导出任务，请稍后重试'
      )
      taskState.status = 'creation_failed'
      taskState.creationError = errorMsg
      updateLiveAnnouncement('creation_failed', `创建失败：${errorMsg}`)
      exportProgress.message = errorMsg
      exportProgress.type = 'error'
      exportProgress.description = errorMsg
      ElMessage.error({ message: errorMsg, duration: 5000, showClose: true })
    }
  } catch (error) {
    console.error('导出失败:', error)
    
    // 提取错误消息
    let errorMessage = '数据导出过程中发生错误，请稍后重试'
    
    if (error.response) {
      // 有响应的错误
      const responseData = error.response.data
      
      // 检查是否为JSON格式的错误响应
      if (typeof responseData === 'object' && responseData !== null && responseData.message) {
        errorMessage = responseData.message
      } else if (typeof responseData === 'string') {
        // 如果是字符串，直接使用
        errorMessage = responseData
      } else {
        // 其他情况，使用默认消息
        errorMessage = error.response.statusText || errorMessage
      }
    } else if (error.message) {
      // 网络错误或其他错误
      errorMessage = error.message
    }
    
    const safeError = safeTaskError(errorMessage, '暂时无法创建导出任务，请稍后重试')
    taskState.status = 'creation_failed'
    taskState.creationError = safeError
    updateLiveAnnouncement('creation_failed', `创建失败：${safeError}`)
    exportProgress.message = safeError
    exportProgress.type = 'error'
    exportProgress.description = safeError
    ElMessage.error({ message: safeError, duration: 6000, showClose: true })
  } finally {
    exporting.value = false
  }
}

function persistTaskReference() {
  if (taskState.id && taskState.capability) {
    sessionStorage.setItem(TASK_STORAGE_KEY, JSON.stringify({
      id: taskState.id,
      capability: taskState.capability,
      summary: taskState.summary
    }))
  }
}

function clearTaskReference() {
  sessionStorage.removeItem(TASK_STORAGE_KEY)
  taskState.id = ''
  taskState.capability = ''
}

function restoreTaskReference() {
  try {
    const task = JSON.parse(sessionStorage.getItem(TASK_STORAGE_KEY) || 'null')
    if (task && task.id && task.capability) {
      taskState.id = task.id
      taskState.capability = task.capability
      taskState.summary = task.summary || null
      taskState.status = 'pending'
      taskState.restored = true
      taskState.liveText = ''
      ElMessage.info({ message: '已恢复上次导出任务', duration: 1500 })
      startTaskPolling()
    }
  } catch (error) {
    clearTaskReference()
  }
}

function stopTaskPolling() {
  if (taskPollTimer) {
    clearInterval(taskPollTimer)
    taskPollTimer = null
  }
  taskState.active = false
  polling = false
}

function updateTaskProgress(task) {
  taskState.status = task.status
  taskState.pollingError = false
  taskState.restored = false
  if (task.status !== 'failed') taskState.failureReason = ''
  exportProgress.show = true
  exportProgress.message = task.message || '正在处理导出任务'
  exportProgress.percentage = task.progress || 0
  exportProgress.currentDate = task.current_date || ''
  exportProgress.currentIndex = task.current_index || 0
  exportProgress.total = task.total_dates || 0
  exportProgress.status = task.status === 'failed' ? 'exception' : ''
  exportProgress.description = task.message || ''

  if (task.status === 'pending') {
    taskState.liveText = ''
    exportProgress.type = 'info'
    return
  }
  if (task.status === 'running') {
    const progressKey = task.current_date
      ? `running:${task.current_date}` : 'running:preparing'
    updateLiveAnnouncement(
      progressKey,
      task.current_date
        ? `正在处理 ${task.current_date}，第 ${task.current_index}/${task.total_dates} 天`
        : '正在准备导出数据'
    )
    exportProgress.type = 'info'
    return
  }

  stopTaskPolling()
  taskState.resultAvailable = Boolean(task.result_available)
  taskState.filename = task.filename || ''
  if (task.status === 'completed') {
    updateLiveAnnouncement(
      task.result_available ? 'completed:download' : 'completed:expired',
      task.result_available ? 'Excel 已生成，可立即下载' : '临时文件已清理，请重新提交导出任务'
    )
    exportProgress.type = 'success'
    exportProgress.message = '导出任务完成'
    exportProgress.description += task.result_available
      ? '\n\n文件已生成，可点击“下载已生成文件”。'
      : '\n\n文件已生成，但下载结果已清理或过期。'
    ElMessage.success('导出任务完成')
  } else {
    taskState.failureReason = safeTaskError(task.message, '导出任务未能完成，请重新提交任务')
    exportProgress.type = 'error'
    exportProgress.message = '导出任务失败'
    exportProgress.description = taskState.failureReason
    updateLiveAnnouncement('failed', `导出失败：${taskState.failureReason}`)
    ElMessage.error(taskState.failureReason)
  }
}

async function pollTaskStatus() {
  if (!taskState.id || polling) return
  polling = true
  try {
    const response = await get_asa_export_task(taskState.id, taskState.capability)
    const payload = response.data
    if (!payload || payload.code !== 200 || !payload.data) {
      throw new Error((payload && payload.message) || '无法获取任务状态')
    }
    updateTaskProgress(payload.data)
  } catch (error) {
    // 短暂网络故障不终止后台任务；保留轮询，下次间隔继续尝试。
    const message = error.response && error.response.data && error.response.data.message
      ? error.response.data.message : error.message
    taskState.pollingError = true
    updateLiveAnnouncement('polling_error', '暂时无法更新进度，将自动重试')
    exportProgress.message = message || '暂时无法更新进度'
    exportProgress.type = 'warning'
    exportProgress.description = '将自动重试，不会中断后台导出'
  } finally {
    polling = false
  }
}

function startTaskPolling() {
  stopTaskPolling()
  taskState.active = true
  pollTaskStatus()
  taskPollTimer = setInterval(pollTaskStatus, 3000)
}

async function downloadTaskResult() {
  if (!taskState.id || !taskState.resultAvailable) return
  try {
    const response = await download_asa_export_task(taskState.id, taskState.capability)
    const data = response.data
    if (data && data.code) {
      throw new Error(data.message || '下载文件失败')
    }
    const filename = taskState.filename || `asa_${taskState.id}.xlsx`
    const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)

    // 浏览器已接收文件后释放 Redis 中的 Base64 缓存，状态记录仍保留至 TTL。
    await clear_asa_export_task_result(taskState.id, taskState.capability)
    taskState.resultAvailable = false
    taskState.downloadStarted = true
    updateLiveAnnouncement('download_started', '文件下载已开始')
    clearTaskReference()
    ElMessage.success('文件下载已开始，服务器临时结果已清理')
  } catch (error) {
    const message = error.response && error.response.data && error.response.data.message
      ? error.response.data.message : error.message
    ElMessage.error(message || '下载文件失败，请稍后重试')
  }
}

onBeforeUnmount(stopTaskPolling)
</script>

<style scoped>
.asa-data-export {
  padding: 20px;
}

.remark-text {
  margin-bottom: 15px;
}

.export-task-card {
  max-width: 760px;
  margin-top: 12px;
  padding: 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}

.task-card-header,
.task-card-status,
.task-progress-meta,
.task-summary {
  display: flex;
  align-items: center;
}

.task-card-header {
  justify-content: space-between;
  gap: 12px;
}

.task-card-status {
  gap: 8px;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
}

.task-status-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  color: #fff;
  background: #2563eb;
  line-height: 1;
}

.is-success .task-status-icon { background: #16a34a; }
.is-warning .task-status-icon { background: #d97706; }
.is-danger .task-status-icon { background: #dc2626; }
.is-muted .task-status-icon { background: #64748b; }

.task-card-main {
  margin-top: 12px;
}

.task-card-main strong {
  color: #1f2937;
  font-size: 16px;
}

.task-card-main p,
.task-current-date,
.task-download-hint {
  margin: 5px 0 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
}

.task-progress-section {
  margin-top: 16px;
}

.task-progress-meta {
  justify-content: space-between;
  margin-bottom: 7px;
  color: #475569;
  font-size: 13px;
}

.task-progress-track {
  height: 12px;
  overflow: hidden;
  border-radius: 999px;
  background: #e8eef9;
}

.task-progress-value {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #2563eb;
  transition: width 200ms ease;
}

.task-pending-line {
  display: flex;
  gap: 5px;
  margin-top: 16px;
}

.task-pending-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #2563eb;
  animation: pending-pulse 1s ease-in-out infinite;
}

.task-pending-dot:nth-child(3) { animation-delay: .15s; }
.task-pending-dot:nth-child(4) { animation-delay: .3s; }

@keyframes pending-pulse {
  50% { opacity: .25; transform: translateY(-2px); }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.task-summary {
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 14px;
}

.task-summary span {
  padding: 3px 8px;
  color: #475569;
  font-size: 12px;
  background: #f1f5f9;
  border-radius: 999px;
}

.task-card-action {
  min-height: 36px;
  flex-shrink: 0;
}

@media (prefers-reduced-motion: reduce) {
  .task-pending-dot { animation: none; }
  .task-progress-value { transition: none; }
}

@media (max-width: 768px) {
  .asa-data-export { padding: 14px; }
  .export-task-card { width: 100%; box-sizing: border-box; }
  .task-card-header { align-items: stretch; flex-direction: column; }
  .task-card-action { width: 100%; }
  .task-card-main strong { font-size: 15px; }
  .task-card-main p,
  .task-current-date,
  .task-download-hint { font-size: 13px; }
}
</style>